import AxiosService from '@/api/ApiBase';
import {
  CommissionsReportFileEntity,
  CourierReportPayload,
  CustomerInvoicesPayload,
  EmptyUnitReportEntity,
  HblLotKpiPayload,
  ImportsHoldReportResponse,
  ImportsMonthlyReportResponse,
  ImportsReportsCfsInventoryGeneratePayload,
  ImportsReportsHoldGeneratePayload,
  ImportsReportsMonthlyGeneratePayload,
  InternalInventoryPayload,
  MblLotKpiPayload,
  ReportFileEntity,
  ReportInvoiceFilters,
  WindowReleasePayload,
  FlatInvoicePayload,
  SalesJournalPayload,
} from '@/types/imports/reports/imports.reports.types';
import { FilterOptionsParams } from '@/types/service/filters.service';
import { useOrganizationStore } from '@/store/modules/useOrganizationStore';
import { computed } from 'vue';
import { DatePeriod } from '@/types/common/dateRange.model';

const IMPORT_REPORTS_BASE_URL = 'warehouse/import/reports';

export interface PeriodReportPayload {
  customer: string[] | string;
  period: DatePeriod;
}

class ReportsService {
  facility = computed(() => useOrganizationStore().currentWarehouse?.code as string);

  generateMonthlyOrigin(payload: ImportsReportsMonthlyGeneratePayload) {
    return AxiosService.post<ImportsMonthlyReportResponse>(
      `${IMPORT_REPORTS_BASE_URL}/monthlyOrigin`,
      payload,
      { headers: { facility: this.facility.value } }
    );
  }

  generateCfsInventory(payload: ImportsReportsCfsInventoryGeneratePayload) {
    return AxiosService.post(`${IMPORT_REPORTS_BASE_URL}/cfsInventory`, payload, {
      headers: { facility: this.facility.value },
    });
  }

  generateHold(payload: ImportsReportsHoldGeneratePayload) {
    return AxiosService.post<ImportsHoldReportResponse>(
      `${IMPORT_REPORTS_BASE_URL}/hold`,
      payload,
      { headers: { facility: this.facility.value } }
    );
  }

  getEmptyUnitReport() {
    return AxiosService.get<EmptyUnitReportEntity>(`warehouse/reports/unitInventory`, {
      headers: { facility: this.facility.value },
    });
  }

  downloadEmptyUnitReport() {
    return AxiosService.get<ReportFileEntity>(`warehouse/reports/download/unitInventory`, {
      headers: { facility: this.facility.value },
    });
  }

  downloadPendingContainersReport() {
    return AxiosService.get<ReportFileEntity>(`warehouse/reports/download/pendingContainers`, {
      headers: { facility: this.facility.value },
    });
  }

  downloadInventoryReport(customer: string) {
    return AxiosService.get<ReportFileEntity>(`warehouse/reports/download/import/inventory`, {
      params: { customer },
      headers: { facility: this.facility.value },
    });
  }

  downloadWarehouseReport(payload: PeriodReportPayload) {
    return AxiosService.get<CommissionsReportFileEntity>(
      `warehouse/reports/commissions/warehouse/collect`,
      {
        params: payload,
        headers: { facility: this.facility.value },
      }
    );
  }

  finalizeWarehouseReport(payload: PeriodReportPayload & { isPending: boolean }) {
    return AxiosService.post<{ message: string }>(
      `warehouse/reports/commissions/warehouse/finalize`,
      payload,
      {
        headers: { facility: this.facility.value },
      }
    );
  }

  downloadCommissionReport(payload: PeriodReportPayload) {
    return AxiosService.get<CommissionsReportFileEntity>(`warehouse/reports/commissions/data`, {
      params: payload,
      headers: { facility: this.facility.value },
    });
  }

  downloadCommissionReportBatch(customer: string[]) {
    return AxiosService.get<BufferSource>(`warehouse/reports/commissions/batch`, {
      params: { customer },
      headers: { facility: this.facility.value },
      responseType: 'blob',
    });
  }

  finalizeCommissionReport(payload: PeriodReportPayload & { isPending: boolean }) {
    return AxiosService.post<{ message: string }>(
      `warehouse/reports/commissions/finalize`,
      payload,
      {
        headers: { facility: this.facility.value },
      }
    );
  }

  downloadInvoiceReport(filters: ReportInvoiceFilters) {
    return AxiosService.get<ReportFileEntity>(`warehouse/reports/download/invoice`, {
      params: { ...filters },
      headers: { facility: this.facility.value },
    });
  }

  getInvoiceFilterOptions<T>(params: Omit<FilterOptionsParams, 'limit'>) {
    return AxiosService.get<T>(`warehouse/reports/invoice/filter/options`, {
      params: { ...params, limit: 100 },
      headers: { facility: this.facility.value },
    });
  }

  downloadInternalInventoryReport(
    { documentType, outputType }: InternalInventoryPayload,
    query = {}
  ) {
    const organizationStore = useOrganizationStore();

    return AxiosService.post(
      `cfs/report/${documentType}/${outputType}`,
      {},
      {
        headers: {
          Sub_Organization: organizationStore.currentWarehouse?.id,
          facility: this.facility.value,
        },
        params: query,
        responseType: 'blob',
      }
    );
  }

  downloadIpiAgingReport() {
    return AxiosService.get('warehouse/reports/ipiAging/download/');
  }

  downloadCustomerInvoicesReport(payload: CustomerInvoicesPayload) {
    return AxiosService.get(`warehouse/reports/customerInvoice/download`, {
      params: payload,
    });
  }

  downloadHblLotKpiReport(payload: HblLotKpiPayload) {
    return AxiosService.get(`warehouse/reports/downloadHblKpiReport`, {
      params: { ...payload },
    });
  }

  downloadMblLotKpiReport(payload: MblLotKpiPayload) {
    return AxiosService.get(`warehouse/reports/downloadMblKpiReport`, {
      params: { ...payload },
    });
  }

  downloadReleasedToGoReport() {
    return AxiosService.get<ReportFileEntity>(`warehouse/reports/downloadReleaseToGoReport`);
  }

  downloadCourierReport(payload: CourierReportPayload) {
    return AxiosService.post(`warehouse/reports/courier/download`, payload);
  }

  finalizeCourierReport(payload: CourierReportPayload) {
    return AxiosService.post(`warehouse/reports/courier/finalize`, payload);
  }

  downloadWindowReleaseReport(payload: WindowReleasePayload) {
    return AxiosService.post(`warehouse/reports/windowRelease/download`, payload);
  }

  downloadFlatInvoiceReport(payload: FlatInvoicePayload) {
    return AxiosService.get(`warehouse/accounting/sage/flat/download`, {
      params: payload,
    });
  }

  downloadSalesJournalReport(payload: SalesJournalPayload) {
    return AxiosService.get<ReportFileEntity>(`warehouse/reports/sales/download`, {
      params: {
        'dateRange[start]': payload.dateRange.start,
        'dateRange[end]': payload.dateRange.end,
        reportBy: payload.reportBy,
      },
      headers: { facility: this.facility.value },
    });
  }

  getWeeklyBillingCustomers(dateRange: { start: string; end: string }) {
    return AxiosService.get(`warehouse/reports/weekly-billing/customers`, {
      params: {
        'dateRange[start]': dateRange.start,
        'dateRange[end]': dateRange.end,
      },
      headers: { facility: this.facility.value },
    });
  }

  getWeeklyBillingDestinations(dateRange: { start: string; end: string }) {
    return AxiosService.get(`warehouse/reports/weekly-billing/destinations`, {
      params: {
        'dateRange[start]': dateRange.start,
        'dateRange[end]': dateRange.end,
      },
      headers: { facility: this.facility.value },
    });
  }

  downloadWeeklyBillingReport(payload: {
    dateRange: { start: string; end: string };
    customers: string[];
    destinations: string[];
  }) {
    const params: Record<string, any> = {
      'dateRange[start]': payload.dateRange.start,
      'dateRange[end]': payload.dateRange.end,
    };

    if (payload.customers.length > 0) {
      params.customer = payload.customers;
    }

    if (payload.destinations.length > 0) {
      params.destination = payload.destinations;
    }

    return AxiosService.get(`warehouse/reports/weekly-billing/download`, {
      params,
      headers: { facility: this.facility.value },
    });
  }

  finalizeWeeklyBillingCharges(payload: {
    customer: string[];
    destination: string[];
    dateRange: { start: string; end: string };
  }) {
    return AxiosService.post(`warehouse/reports/weekly-billing/finalize`, payload, {
      headers: { facility: this.facility.value },
    });
  }
}

export default new ReportsService();
